// Per P-037.29 post-Phase-Close §item F (Banks's text-Block top-padding fix)
// — REVISED P-037.30 §item A (Tiptap single- vs double-Enter parity).
// Renders a DOMPurify-sanitized richText HtmlString as inline-styled top-
// level block elements — `<p>`, `<ul>`, `<ol>` — WITHOUT wrapping the
// body in an outer `<p>`.
//
// **Why this exists.** The pre-fix path piped richText bodies through the
// `text()` primitive: `text({ text: sanitizeRichText(body), size: 14 })`
// — which wraps the body in `<p style="margin: 0; …">…</p>`. Because the
// sanitized body itself contains `<p>` tags from Tiptap, the rendered HTML
// became a nested `<p>` inside a `<p>`:
//
//     <p style="margin: 0; font-size: 14px; …">
//       <p>Body text</p>
//     </p>
//
// HTML5 parsing closes the outer `<p>` when the inner opens (`<p>` cannot
// contain `<p>`), so the browser actually sees:
//
//     <p style="…"></p>          <!-- empty outer; styling wasted -->
//     <p>Body text</p>           <!-- UA-default font + 1em top margin -->
//
// Net visible side-effects: the body inherited UA-default 16 px Times,
// `line-height: normal`, black color, AND a 1em top margin showed up as
// ~14–16 px of "phantom" top padding on every text / time-location-cost
// Block. The 14 px / 1.5 / #1f2937 styling never reached the rendered
// paragraph.
//
// **The fix.** Walk the sanitized body's top-level block tags (`<p>`,
// `<ul>`, `<ol>`) and inject styling directly:
//
//   - Every top-level block: `margin: 0` (P-037.30 §A — see below)
//   - All get font-family / font-size / line-height / color / text-align
//   - `<ul>` / `<ol>` also get `padding-left: 24px` so the bullets/numbers
//     render visually nested (Tiptap UI renders them indented; UA-default
//     `padding-inline-start: 40px` is inconsistent across email clients).
//
// **P-037.30 §item A — single- vs double-Enter parity with the Tiptap
// editor.** The Tiptap config disables `hardBreak`, so the ONLY way staff
// can break lines is `Enter` (= new `<p>`) or `Enter Enter` (= empty `<p>`
// between content `<p>`s). The pre-fix output gave every subsequent top-
// level block `margin: 14px 0 0 0`, which collapsed both single- and
// double-Enter into the same visible gap in the rendered email — staff
// couldn't visually distinguish a line break from a paragraph break.
// With every block at `margin: 0`:
//
//   - Single Enter (`<p>a</p><p>b</p>`) → adjacent line-boxes, no margin
//     between → tight gap, reads like a line break.
//   - Double Enter (`<p>a</p><p></p><p>b</p>`) → the middle empty `<p>`
//     reserves its own ~21 px line-box (font-size 14 × line-height 1.5),
//     producing a visible blank line between the two content `<p>`s.
//
// **Empty `<p></p>` → `<p>&nbsp;</p>` rewrite (also §item A).** Tiptap
// emits an empty `<p></p>` for the gap paragraph in a double-Enter
// sequence (verified — see `sanitize.test.ts`-style scratch). Modern
// browsers reserve a line-box for an empty styled block element, but
// Outlook's Word renderer and several Gmail rendering paths collapse
// truly-empty `<p>`s to 0 height — which would erase the blank-line
// gap in the very email clients we ship to. Injecting `&nbsp;` forces
// a character into the line-box, guaranteeing the 21 px gap across
// every client. The `&nbsp;` is the canonical email-HTML pattern for
// this (used elsewhere in this codebase for spacer `<td>`s — see
// `time-location-cost.ts` spacer rows). DOMPurify's allow-list keeps
// `&nbsp;` (an HTML entity, not a tag) intact in the input pipeline.
//
// **No `<li>` styling.** `<li>` tags appear only INSIDE `<ul>` / `<ol>`,
// so they're not top-level blocks. The parent list's font / color cascades
// to its children via CSS inheritance.
//
// **Trust assumption.** Input is already DOMPurify-sanitized (per the
// strict allow-list in `src/render/sanitize.ts`): the only tags that can
// appear are `p`, `strong`, `em`, `a`, `br`, `ul`, `ol`, `li`, and the
// only attributes that survive are `href` / `target` / `rel` on `<a>`.
// DOMPurify strips ALL other attributes including `style=`, so the `<p>`,
// `<ul>`, `<ol>` opening tags will never have an existing `style="…"` to
// merge with — we can safely inject a fresh `style="…"` attribute.
//
// **P-048.1 D-144 (Item 1 + Item 3) — inline font-family + font-weight on
// inner tags.** Banks's first real-send-to-Gmail smoke surfaced two
// rendering bugs traced to the document-shell `<style>` cascade being
// stripped by Outlook desktop / Yahoo / Gmail's stricter rendering paths
// (D-113 Layer 2): (a) body paragraphs rendered serif because nested
// inline tags (`<strong>`, `<em>`, `<a>`, `<li>`) inside richText
// inherited the UA-default font-family rather than the document body's
// inline font-family; (b) `<strong>` tags rendered non-bold because the
// surrounding `<p style="font-weight: 400">` cascaded into `<strong>`'s
// implicit boldness in clients that flatten inheritance. Fix: walk every
// inner tag opener (bare for `<strong>` / `<em>` / `<li>` — DOMPurify
// strips all attrs; attr-laden for `<a>` which keeps `href` / `target` /
// `rel`) and inject inline `font-family` + tag-specific bold/italic.
// Top-level `<p>` / `<ul>` / `<ol>` walks (which already injected these)
// are unchanged. The inner tags don't need full typography (size /
// line-height / color cascade from the parent block via CSS inheritance
// when font-family is honored); only the properties that fail under
// stripped-`<style>` rendering get inlined.

import { escapeHtml } from './html'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

export interface RichTextBodyOpts {
  /** Body font size in px. Default `tokens.type.size.base` (16); most callers pass 14. */
  size?: number
  /** Font weight. Default `tokens.type.weight.regular` (400). */
  weight?: number
  /** Text color (hex or named). Default `tokens.color['text-primary']` (#1f2937). */
  color?: string
  /** Line-height (unit-less). Default `tokens.type.leading.normal` (1.5). */
  lineHeight?: number
  /**
   * Padding-left in px applied to `<ul>` / `<ol>` so the bullets/numbers
   * render visually nested. Default 24.
   */
  listPaddingLeftPx?: number
}

// P-048.1 D-144 (Item 1) — per-inner-tag style fragments. The font-family
// + tag-specific declaration is the minimum needed to survive Gmail's
// stripped-`<style>` rendering; size / line-height / color cascade from
// the parent block via CSS inheritance once font-family is honored.
const FONT_FAMILY_STYLE = `font-family: ${tokens.type.fontFamily};`
const STRONG_STYLE = escapeHtml(`${FONT_FAMILY_STYLE} font-weight: bold;`)
const EM_STYLE = escapeHtml(`${FONT_FAMILY_STYLE} font-style: italic;`)
const ANCHOR_STYLE = escapeHtml(FONT_FAMILY_STYLE)
const LI_STYLE = escapeHtml(FONT_FAMILY_STYLE)

// Match an opening tag for any of the three top-level block elements:
// `<p>`, `<ul>`, `<ol>`. DOMPurify strips all attributes (no `style=`,
// `class=`, `id=`, etc. survive on these tags — they're not in
// ALLOWED_ATTR), so a bare opener is the only shape we encounter.
const TOP_LEVEL_BLOCK_OPENER = /<(p|ul|ol)>/g

// P-048.1 D-144 (Item 1 + Item 3) — inner bare-opener tags. DOMPurify
// strips all attrs from these too, so the bare-opener regex shape is
// safe. `<strong>` gets inline `font-weight: bold`; `<em>` gets inline
// `font-style: italic`; `<li>` inherits typography from the parent
// `<ul>` / `<ol>` (which already carries the inline typography from
// the top-level walk) but still gets explicit `font-family` so Gmail's
// stripped-`<style>` rendering doesn't fall back to UA serif on each
// list item.
const INNER_BARE_OPENER = /<(strong|em|li)>/g

// P-048.1 D-144 (Item 1) — anchor opener with attrs. DOMPurify keeps
// `href` / `target` / `rel` on `<a>`; everything else strips. The capture
// group preserves whatever attrs survived so we can inject `style="…"`
// at the end without rewriting the existing attrs. The `\s` ensures the
// match only fires on `<a ` followed by attrs (or `<a>` bare — handled
// by the `?` quantifier on the capture group).
const ANCHOR_OPENER = /<a(\s[^>]*)?>/g

// Match an empty `<p></p>` (the Tiptap double-Enter gap-paragraph shape).
// DOMPurify preserves these intact, so they reach this helper. We rewrite
// to `<p>&nbsp;</p>` to force a line-box across email clients — see file
// header for the rationale.
const EMPTY_PARAGRAPH = /<p><\/p>/g

/**
 * Render a DOMPurify-sanitized richText body as inline-styled top-level
 * block elements. See file header for the design rationale.
 *
 * Walks the sanitized body's `<p>`, `<ul>`, `<ol>` opening tags (the
 * only top-level block shapes in the sanitizer's allow-list) and rewrites
 * each as `<tag style="…">` with `margin: 0`. Lists also get a `padding-
 * left`. Empty `<p></p>` (Tiptap's double-Enter gap-paragraph shape) is
 * rewritten to `<p>&nbsp;</p>` BEFORE the opener walk, so it carries the
 * same `margin: 0` + typography styling and reserves a line-box across
 * email clients. `<li>` tags are NOT top-level; they're untouched
 * (font/color cascade from the parent list).
 *
 * Returns an `HtmlString` because the body itself is already trusted
 * (DOMPurify-sanitized) and the injected styles contain only literal
 * design-token values — no caller-supplied data flows into the style
 * string un-escaped.
 */
export function richTextBody(body: HtmlString, opts: RichTextBodyOpts = {}): HtmlString {
  const {
    size = tokens.type.size.base,
    weight = tokens.type.weight.regular,
    color = tokens.color['text-primary'],
    lineHeight = tokens.type.leading.normal,
    listPaddingLeftPx = 24,
  } = opts

  // Empty body short-circuits — sanitizeRichText('') returns an empty
  // HtmlString; nothing to inject styles on.
  if (body.value === '') return new HtmlString('')

  // `escapeHtml` on the typography chunk mirrors what the `html` kernel
  // does on interpolated plain-string style attributes elsewhere in the
  // codebase: single quotes inside `tokens.type.fontFamily` (the font-
  // stack quoting around `'Helvetica Neue'`) become `&#39;` in the
  // rendered output. Email clients parse the entity back to a literal
  // single quote identically to the source form, so this is byte-shape
  // alignment, not a behavioral choice — see D-029.
  const baseTypography = escapeHtml(
    `font-family: ${tokens.type.fontFamily}; font-size: ${size}px; ` +
      `font-weight: ${weight}; line-height: ${lineHeight}; color: ${color}; text-align: left;`,
  )

  // P-037.30 §A: rewrite empty `<p></p>` to `<p>&nbsp;</p>` BEFORE the
  // opener walk so the gap-paragraph picks up the same inline styling as
  // every other `<p>`. Order matters — the opener walk matches `<p>`
  // bare (no closing-tag context), so it would style the empty `<p>`
  // opener correctly either way, but doing the empty-rewrite first keeps
  // the regex simple and the `&nbsp;` reaches the rendered output.
  //
  // P-048.1 D-144 (Item 1 + Item 3): chain three additional walks after
  // the top-level walk to inject inline styling on `<strong>` / `<em>` /
  // `<a>` / `<li>` so Gmail's stripped-`<style>` rendering doesn't fall
  // back to UA serif (font-family) or strip the implicit boldness on
  // `<strong>` (font-weight). Each walk operates on the output of the
  // previous; the regexes don't interfere because they target distinct
  // tag-name shapes. Order: EMPTY_PARAGRAPH → TOP_LEVEL_BLOCK_OPENER →
  // INNER_BARE_OPENER → ANCHOR_OPENER.
  const rewritten = body.value
    .replace(EMPTY_PARAGRAPH, '<p>&nbsp;</p>')
    .replace(TOP_LEVEL_BLOCK_OPENER, (_match, tag: string) => {
      const listPadding =
        tag === 'ul' || tag === 'ol' ? ` padding-left: ${listPaddingLeftPx}px;` : ''
      return `<${tag} style="margin: 0; ${baseTypography}${listPadding}">`
    })
    .replace(INNER_BARE_OPENER, (_match, tag: string) => {
      const style = tag === 'strong' ? STRONG_STYLE : tag === 'em' ? EM_STYLE : LI_STYLE
      return `<${tag} style="${style}">`
    })
    .replace(ANCHOR_OPENER, (_match, attrs: string | undefined) => {
      return `<a${attrs ?? ''} style="${ANCHOR_STYLE}">`
    })

  return new HtmlString(rewritten)
}
